import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Calendar, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Events() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState('all'); // all, published, draft, closed

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const filteredEvents = events.filter(e => filter === 'all' || e.status === filter);

  return (
    <Layout>
      <div className="space-y-6" data-testid="events-page">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Eventi</h1>
          <Button onClick={() => navigate('/events/new')} data-testid="new-event-button">
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Evento
          </Button>
        </div>

        <div className="flex gap-2">
          {['all', 'published', 'draft', 'closed'].map(status => (
            <Button
              key={status}
              variant={filter === status ? 'default' : 'outline'}
              onClick={() => setFilter(status)}
              size="sm"
              data-testid={`filter-${status}-button`}
            >
              {status === 'all' ? 'Tutti' : 
               status === 'published' ? 'Pubblicati' :
               status === 'draft' ? 'Bozze' : 'Chiusi'}
            </Button>
          ))}
        </div>

        {filteredEvents.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center" data-testid="no-events-found">
                <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun evento trovato</h3>
                <p className="text-gray-600 mb-4">
                  {filter === 'all' ? 'Crea il tuo primo evento' : `Nessun evento con stato "${filter}"`}
                </p>
                {filter === 'all' && (
                  <Button onClick={() => navigate('/events/new')} data-testid="create-event-empty-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Crea Evento
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEvents.map(event => (
              <Card
                key={event.id}
                className="overflow-hidden hover:shadow-lg cursor-pointer border-2 border-transparent hover:border-blue-500"
                onClick={() => navigate(`/events/${event.id}/manage`)}
                data-testid={`event-card-${event.id}`}
              >
                {event.cover_image_url && (
                  <img
                    src={event.cover_image_url}
                    alt={event.title}
                    className="w-full h-48 object-cover"
                  />
                )}
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-xl font-semibold text-gray-900">{event.title}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      event.status === 'published' ? 'bg-green-100 text-green-800' :
                      event.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {event.status === 'published' ? 'Pubblicato' : 
                       event.status === 'draft' ? 'Bozza' : 'Chiuso'}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{event.description}</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <Calendar className="w-4 h-4 mr-2" />
                      {format(new Date(event.start_date), 'dd MMM yyyy, HH:mm', { locale: it })}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2" />
                      {event.location}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
